import React, { useEffect, useMemo, useState } from "react";
import {
  subscribeCategories,
  subscribeSubcategories,
  subscribeServiceItems,
} from "../../lib/servicesApi";
import { ensureSignedIn } from "../../lib/authSession";
import { createEmployeeAdmin } from "../../lib/employeesApi";
import DocumentScanModal from "../../Components/DocumentScanModal";
import { uploadToStorage } from "../../utils/storage";

// Location datasets
import locationNation from "../../data/locationNation.json"; // states & districts
import locationData from "../../data/locationData.json"; // districts -> mandals

const AUTHORIZED_ID_TYPES = [
  "Aadhaar",
  "PAN",
  "VoterID",
  "Driving License",
  "Passport",
  "EShram",
  "Ration Card",
  "Employee ID",
  "Other",
];

const decodeEntities = (str) =>
  String(str || "")
    .replace(/&amp;amp;amp;/g, "&amp;amp;")
    .replace(/&amp;amp;lt;/g, "&amp;lt;")
    .replace(/&amp;amp;gt;/g, "&amp;gt;")
    .replace(/&amp;amp;quot;/g, '"')
    .replace(/&amp;amp;#039;/g, "'");

const computeAgeYearsLocal = (dobStr) => {
  if (!dobStr) return "";
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age >= 0 ? String(age) : "";
};

// ---- helpers to read mandals irrespective of exact JSON shape ----
function buildMandalsIndex(raw) {
  const byDistrict = new Map();

  // Shape A: { districts: [ { district, mandals } ] }
  if (raw?.districts && Array.isArray(raw.districts)) {
    raw.districts.forEach((d) => {
      const name = (d?.district || "").trim();
      const list = Array.isArray(d?.mandals) ? d.mandals : [];
      if (name) byDistrict.set(name, list);
    });
    return byDistrict;
  }

  // Shape B: { states: [ { state, districts: [ { district, mandals } ] } ] }
  if (raw?.states && Array.isArray(raw.states)) {
    raw.states.forEach((s) => {
      (s?.districts || []).forEach((d) => {
        const name = (d?.district || "").trim();
        const list = Array.isArray(d?.mandals) ? d.mandals : [];
        if (name) byDistrict.set(name, list);
      });
    });
    return byDistrict;
  }

  // Fallback (empty)
  return byDistrict;
}

const mandalsByDistrictBase = buildMandalsIndex(locationData);

export default function EmployeesAddForm() {
  // data sources
  const [cats, setCats] = useState([]);
  const [catError, setCatError] = useState(null);
  const [subcats, setSubcats] = useState([]);
  const [subcatError, setSubcatError] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesError, setServicesError] = useState(null);

  useEffect(() => subscribeCategories(setCats, setCatError), []);
  useEffect(() => subscribeSubcategories(setSubcats, setSubcatError), []);
  useEffect(
    () => subscribeServiceItems?.(setServices, setServicesError, {}),
    []
  );

  const activeCats = useMemo(() => cats.filter((c) => c.active), [cats]);

  const subcatsByCat = useMemo(() => {
    const map = new Map();
    subcats.forEach((s) => {
      const arr = map.get(s.categoryKey) || [];
      arr.push(s);
      map.set(s.categoryKey, arr);
    });
    for (const [k, v] of map) {
      v.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      map.set(k, v);
    }
    return map;
  }, [subcats]);

  const jobTitlesByCatSub = useMemo(() => {
    const map = new Map();
    (services || [])
      .filter((s) => s?.isActive !== false)
      .forEach((s) => {
        const catKey = s.category || "";
        const subKey = s.subcategory || "";
        const key = `${catKey}|${subKey}`;
        const list = map.get(key) || [];
        list.push({ value: s.slug || s.name, label: s.name || s.slug || "-" });
        map.set(key, list);
      });
    for (const [k, v] of map) {
      v.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      map.set(k, v);
    }
    return map;
  }, [services]);

  // Location selectors
  const statesList = useMemo(
    () => (locationNation?.states || []).map((s) => s.state),
    []
  );
  const districtsByState = useMemo(() => {
    const map = new Map();
    (locationNation?.states || []).forEach((s) => {
      map.set(s.state, s.districts || []);
    });
    return map;
  }, []);
  const districtsFor = (state) => districtsByState.get(state) || [];

  // ===== Custom Mandal state (in-memory) ===== // NEW
  const [customMandalMode, setCustomMandalMode] = useState(false); // NEW
  const [customMandalInput, setCustomMandalInput] = useState(""); // NEW
  const [customMandalsMap, setCustomMandalsMap] = useState(new Map()); // NEW

  // form state
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createAuthAccount, setCreateAuthAccount] = useState(false);

  const [scanModal, setScanModal] = useState({
    show: false,
    title: "",
    target: null,
    idIndex: null,
    file: null,
    options: {},
  });

  const [form, setForm] = useState({
    uid: "",
    firstName: "",
    lastName: "",
    fatherName: "",
    houseNo: "",
    area: "",
    town: "",
    mandal: "",
    district: "",
    state: "",
    pinCode: "",
    mobile: "",
    aadhaar: "",
    pan: "",
    ids: [
      { type: "", value: "", imageUrl: "" },
      { type: "", value: "", imageUrl: "" },
    ],
    email: "",
    password: "",
    dateOfBirth: "",
    ageYears: "",
    experienceYears: "",
    profilePhotoUrl: "",
    jobs: [
      { title: "", categoryKey: "", subcategoryKey: "", experienceYears: "" },
    ],
    categoryKey: "",
    subcategoryKey: "",
    status: "pending",
    role: "employee",
  });

  // defaults
  useEffect(() => {
    const firstCatKey = activeCats[0]?.key;
    if (!firstCatKey) return;
    setForm((f) => {
      if (f.jobs[0]?.categoryKey) return f;
      const jobs = [...f.jobs];
      const subs = subcatsByCat.get(firstCatKey) || [];
      const firstSubKey = subs[0]?.key || "";
      const titles =
        jobTitlesByCatSub.get(`${firstCatKey}|${firstSubKey}`) || [];
      const firstTitle = titles[0]?.label || "";
      jobs[0] = {
        ...jobs[0],
        categoryKey: firstCatKey,
        subcategoryKey: firstSubKey,
        title: firstTitle,
      };
      return {
        ...f,
        jobs,
        categoryKey: firstCatKey,
        subcategoryKey: firstSubKey,
      };
    });
  }, [activeCats, subcatsByCat, jobTitlesByCatSub]);

  // Reset custom mandal UI when district changes // NEW
  useEffect(() => {
    setCustomMandalMode(false);
    setCustomMandalInput("");
    // NOTE: we do not auto-clear form.mandal here, because select onChange already clears it
  }, [form.district]);

  // handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleIdChange = (idx, key, value) => {
    setForm((f) => {
      const ids = [...f.ids];
      const v = typeof value === "string" ? value.trim() : value;
      ids[idx] = { ...ids[idx], [key]: v };
      return { ...f, ids };
    });
  };

  const addIdRow = () =>
    setForm((f) => ({
      ...f,
      ids: [...f.ids, { type: "", value: "", imageUrl: "" }],
    }));
  const removeIdRow = (index) =>
    setForm((f) => ({ ...f, ids: f.ids.filter((_, i) => i !== index) }));

  const handleJobChange = (index, key, value) => {
    setForm((f) => {
      const jobs = [...f.jobs];
      jobs[index] = { ...jobs[index], [key]: value };
      return { ...f, jobs };
    });
  };

  const addJobRow = () =>
    setForm((f) => ({
      ...f,
      jobs: [
        ...f.jobs,
        { title: "", categoryKey: "", subcategoryKey: "", experienceYears: "" },
      ],
    }));

  const removeJobRow = (index) =>
    setForm((f) => {
      const jobs = f.jobs.filter((_, i) => i !== index);
      return {
        ...f,
        jobs: jobs.length
          ? jobs
          : [
              {
                title: "",
                categoryKey: "",
                subcategoryKey: "",
                experienceYears: "",
              },
            ],
      };
    });

  const onJobCategoryChange = (index, categoryKey) => {
    const subs = subcatsByCat.get(categoryKey) || [];
    const newSubKey = subs[0]?.key || "";
    setForm((f) => {
      const jobs = [...f.jobs];
      jobs[index] = { ...jobs[index], categoryKey, subcategoryKey: newSubKey };
      return { ...f, jobs };
    });
    const titles = jobTitlesByCatSub.get(`${categoryKey}|${newSubKey}`) || [];
    const firstTitle = titles[0]?.label || "";
    setForm((f) => {
      const jobs = [...f.jobs];
      jobs[index] = { ...jobs[index], title: firstTitle };
      return { ...f, jobs };
    });
  };

  const onJobSubcategoryChange = (index, subcategoryKey) => {
    setForm((f) => {
      const jobs = [...f.jobs];
      jobs[index] = { ...jobs[index], subcategoryKey };
      return { ...f, jobs };
    });
    const categoryKey = form.jobs[index]?.categoryKey || "";
    const titles =
      jobTitlesByCatSub.get(`${categoryKey}|${subcategoryKey}`) || [];
    const firstTitle = titles[0]?.label || "";
    setForm((f) => {
      const jobs = [...f.jobs];
      jobs[index] = { ...jobs[index], title: firstTitle };
      return { ...f, jobs };
    });
  };

  const onCreateEmployee = async (e) => {
    e.preventDefault();
    setCreateError(null);

    if (!form.firstName || !form.mobile) {
      setCreateError(new Error("First name and mobile are required."));
      return;
    }
    if (
      !form.houseNo ||
      !form.area ||
      !form.town ||
      !form.district ||
      !form.pinCode ||
      !form.state
    ) {
      setCreateError(
        new Error(
          "Please fill House No, Area, Town, State, District and PIN Code."
        )
      );
      return;
    }

    const normalizeId = (r = {}) => ({
      type: String(r.type || "").trim(),
      value: String(r.value || "").trim(),
      imageUrl: String(r.imageUrl || "").trim(),
    });
    const completeIds = (form.ids || [])
      .map(normalizeId)
      .filter((r) => r.type && r.value && r.imageUrl);
    if (completeIds.length < 2) {
      setCreateError(
        new Error(
          "Please provide at least 2 Authorized IDs with images (type, number, and uploaded photo)."
        )
      );
      return;
    }

    for (const j of form.jobs || []) {
      const hasCatOrSub =
        (j.categoryKey && j.categoryKey.trim()) ||
        (j.subcategoryKey && j.subcategoryKey.trim());
      if (hasCatOrSub && !String(j.title || "").trim()) {
        setCreateError(
          new Error("Please select a Job Title for each job row.")
        );
        return;
      }
    }

    const normalizedJobs = (form.jobs || [])
      .map((j) => ({
        title: String(j.title || "").trim(),
        categoryKey: j.categoryKey || "",
        subcategoryKey: j.subcategoryKey || "",
        experienceYears:
          j.experienceYears === "" ? null : Number(j.experienceYears),
      }))
      .filter(
        (j) =>
          j.title ||
          j.categoryKey ||
          j.subcategoryKey ||
          (j.experienceYears !== null && !Number.isNaN(j.experienceYears))
      );

    const primaryJob = normalizedJobs[0] || {
      title: "",
      categoryKey: form.categoryKey || "",
      subcategoryKey: form.subcategoryKey || "",
      experienceYears:
        form.experienceYears === "" ? null : Number(form.experienceYears),
    };

    const ageYearsDisplay = form.ageYears === "" ? "" : Number(form.ageYears);
    const experienceYearsTotal =
      form.experienceYears === "" ? null : Number(form.experienceYears);

    const combinedAddress = [
      `House No: ${form.houseNo}`,
      `Area: ${form.area}`,
      `Town: ${form.town}`,
      form.mandal ? `Mandal: ${form.mandal}` : null,
      `District: ${form.district}`,
      `State: ${form.state}`,
      `PIN: ${form.pinCode}`,
    ]
      .filter(Boolean)
      .join(", ");

    setCreating(true);
    try {
      await createEmployeeAdmin(
        {
          uid: form.uid || undefined,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fatherName: form.fatherName.trim(),

          houseNo: form.houseNo.trim(),
          area: form.area.trim(),
          town: form.town.trim(),
          mandal: form.mandal.trim() || undefined,
          district: form.district.trim(),
          state: form.state.trim(),
          pinCode: form.pinCode.trim(),

          address: combinedAddress,

          mobile: form.mobile.trim(),
          aadhaar: form.aadhaar.trim(),
          pan: form.pan.trim(),

          ids: completeIds,

          email: form.email || undefined,
          password: form.password || undefined,

          dateOfBirth: form.dateOfBirth || undefined,
          ageYears:
            ageYearsDisplay === "" || Number.isNaN(ageYearsDisplay)
              ? undefined
              : ageYearsDisplay,
          experienceYears:
            experienceYearsTotal === null || Number.isNaN(experienceYearsTotal)
              ? undefined
              : experienceYearsTotal,

          profilePhotoUrl: form.profilePhotoUrl || undefined,

          jobs: normalizedJobs,
          categoryKey: primaryJob.categoryKey || undefined,
          subcategoryKey: primaryJob.subcategoryKey || undefined,
          status: "pending",
          role: "employee",
        },
        { createAuth: !!createAuthAccount }
      );

      // reset to defaults
      const firstCatKey = activeCats[0]?.key || "";
      const firstSubKey = (subcatsByCat.get(firstCatKey) || [])[0]?.key || "";
      const firstTitle =
        (jobTitlesByCatSub.get(`${firstCatKey}|${firstSubKey}`) || [])[0]
          ?.label || "";

      setForm({
        uid: "",
        firstName: "",
        lastName: "",
        fatherName: "",
        houseNo: "",
        area: "",
        town: "",
        mandal: "",
        district: "",
        state: "",
        pinCode: "",
        mobile: "",
        aadhaar: "",
        pan: "",
        ids: [
          { type: "", value: "", imageUrl: "" },
          { type: "", value: "", imageUrl: "" },
        ],
        email: "",
        password: "",
        dateOfBirth: "",
        ageYears: "",
        experienceYears: "",
        profilePhotoUrl: "",
        jobs: [
          {
            title: firstTitle,
            categoryKey: firstCatKey,
            subcategoryKey: firstSubKey,
            experienceYears: "",
          },
        ],
        categoryKey: firstCatKey,
        subcategoryKey: firstSubKey,
        status: "pending",
      });

      // Also clear any custom mandals for a fresh page if you want
      setCustomMandalsMap(new Map());
      setCustomMandalMode(false);
      setCustomMandalInput("");

      alert("Employee saved. You can review it under Requests.");
    } catch (err) {
      console.error("Create employee failed:", err);
      setCreateError(err);
    } finally {
      setCreating(false);
    }
  };

  // ---- UI ----

  // Merge base mandals + custom (per district) // NEW
  const baseMandals = mandalsByDistrictBase.get(form.district) || [];
  const customMandals = customMandalsMap.get(form.district) || [];
  const mandalsOptions = [...baseMandals, ...customMandals]; // NEW

  const districtsOptions = districtsFor(form.state);

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <h5 className="card-title">Add Employee</h5>
        {catError && (
          <div className="alert alert-danger">
            Categories: {catError.message}
          </div>
        )}
        {subcatError && (
          <div className="alert alert-danger">
            Subcategories: {subcatError.message}
          </div>
        )}
        {servicesError && (
          <div className="alert alert-warning">
            Services: {servicesError.message}. Job Titles may not load.
          </div>
        )}

        <form onSubmit={onCreateEmployee}>
          <div className="d-flex flex-row gap-3">
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="form-control"
                value={form.email}
                onChange={handleFormChange}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                className="form-control"
                value={form.password}
                onChange={handleFormChange}
                autoComplete="new-password"
              />
            </div>
            <div className="col-12 col-md-4 d-flex align-items-end">
              <div className="form-check">
                <input
                  id="create-auth"
                  className="form-check-input"
                  type="checkbox"
                  checked={createAuthAccount}
                  onChange={(e) => setCreateAuthAccount(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="create-auth">
                  Create login account
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {/* Name */}
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="firstName">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                className="form-control"
                value={form.firstName}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="lastName">
                Surname
              </label>
              <input
                id="lastName"
                name="lastName"
                className="form-control"
                value={form.lastName}
                onChange={handleFormChange}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="fatherName">
                Father Name
              </label>
              <input
                id="fatherName"
                name="fatherName"
                className="form-control"
                value={form.fatherName}
                onChange={handleFormChange}
              />
            </div>

            {/* Address */}
            <div className="col-12">
              <h6 className="mb-2">Address</h6>
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label" htmlFor="houseNo">
                House No
              </label>
              <input
                id="houseNo"
                name="houseNo"
                className="form-control"
                value={form.houseNo}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label" htmlFor="area">
                Area
              </label>
              <input
                id="area"
                name="area"
                className="form-control"
                value={form.area}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label" htmlFor="town">
                Town
              </label>
              <input
                id="town"
                name="town"
                className="form-control"
                value={form.town}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="d-flex flex-row gap-5">
              {/* State */}
              <div className="col-12 col-md-2">
                <label className="form-label" htmlFor="state">
                  State
                </label>
                <select
                  id="state"
                  name="state"
                  className="form-select"
                  value={form.state}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      state: e.target.value,
                      district: "",
                      mandal: "",
                    }))
                  }
                  required
                >
                  <option value="">(Select)</option>
                  {statesList.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {/* District */}
              <div className="col-12 col-md-2">
                <label className="form-label" htmlFor="district">
                  District
                </label>
                <select
                  id="district"
                  name="district"
                  className="form-select"
                  value={form.district}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      district: e.target.value,
                      mandal: "",
                    }))
                  }
                  required
                  disabled={!form.state}
                >
                  {!form.state ? (
                    <option value="">— Select State first —</option>
                  ) : districtsOptions.length === 0 ? (
                    <option value="">— No districts found —</option>
                  ) : (
                    <>
                      <option value="">(Select)</option>
                      {districtsOptions.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* Mandal with custom option */}
              <div className="col-12 col-md-2">
                <label className="form-label" htmlFor="mandal">
                  Mandal <small> (optional)</small>
                </label>
                <select
                  id="mandal"
                  name="mandal"
                  className="form-select"
                  value={form.mandal}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__ADD_CUSTOM__") {
                      setCustomMandalMode(true);
                      setCustomMandalInput("");
                      // Clear current mandal selection so Save will set it explicitly
                      setForm((f) => ({ ...f, mandal: "" }));
                    } else {
                      setForm((f) => ({ ...f, mandal: v }));
                    }
                  }}
                  disabled={!form.district}
                >
                  {!form.district ? (
                    <option value="">— Select District first —</option>
                  ) : mandalsOptions.length === 0 ? (
                    <>
                      <option value="">— No mandals in list —</option>
                      <option value="__ADD_CUSTOM__">
                        + Add custom mandal…
                      </option>
                    </>
                  ) : (
                    <>
                      <option value="">(None)</option>
                      {mandalsOptions.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                      <option value="__ADD_CUSTOM__">
                        + Add custom mandal…
                      </option>
                    </>
                  )}
                </select>

                {/* Inline custom mandal editor */}
                {customMandalMode && form.district && (
                  <div className="mt-2">
                    <div className="input-group input-group-sm">
                      <div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter mandal name"
                          value={customMandalInput}
                          onChange={(e) => setCustomMandalInput(e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          const val = (customMandalInput || "").trim();
                          if (!val) return;

                          // Add to custom map for this district (in-memory)
                          setCustomMandalsMap((prev) => {
                            const next = new Map(prev);
                            const list = [...(next.get(form.district) || [])];
                            if (!list.includes(val)) list.push(val);
                            next.set(form.district, list);
                            return next;
                          });

                          // Select it
                          setForm((f) => ({ ...f, mandal: val }));

                          // Close editor
                          setCustomMandalMode(false);
                          setCustomMandalInput("");
                        }}
                      >
                        Save mandal
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setCustomMandalMode(false);
                          setCustomMandalInput("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {/* <small className="text-muted">
                    This mandal will be available until you refresh the page. To
                    make it permanent for everyone, update{" "}
                    <code>src/data/locationData.json</code>.
                  </small> */}
                  </div>
                )}
              </div>

              <div className="col-12 col-md-2">
                <label className="form-label" htmlFor="pinCode">
                  PIN Code
                </label>
                <input
                  id="pinCode"
                  name="pinCode"
                  className="form-control"
                  pattern="^\\d{6}$"
                  title="Enter 6-digit PIN Code"
                  value={form.pinCode}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>

            {/* Contact + Govt IDs */}
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="mobile">
                Mobile Number
              </label>
              <input
                id="mobile"
                name="mobile"
                className="form-control"
                value={form.mobile}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* IDs list */}
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">
                  Authorized IDs (min 2)
                </label>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addIdRow}
                >
                  + Add ID
                </button>
              </div>

              {form.ids.map((row, i) => (
                <div className="row g-2 align-items-end mb-2" key={i}>
                  <div className="col-12 col-md-3">
                    <label className="form-label">ID Type</label>
                    <select
                      className="form-select"
                      value={row.type}
                      onChange={(e) =>
                        handleIdChange(i, "type", e.target.value)
                      }
                    >
                      <option value="">(Select)</option>
                      {AUTHORIZED_ID_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label">ID Number</label>
                    <input
                      className="form-control"
                      placeholder="Enter ID number"
                      value={row.value}
                      onChange={(e) =>
                        handleIdChange(i, "value", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">Upload ID Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="form-control"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setScanModal({
                          show: true,
                          title: "Scan Authorized ID",
                          target: "id",
                          idIndex: i,
                          file: f,
                          options: {
                            maxWidth: 1600,
                            maxHeight: 1600,
                            maxBytes: 500 * 1024,
                            mimeType: "image/jpeg",
                            initialQuality: 0.85,
                          },
                        });
                      }}
                    />
                    {row.imageUrl && (
                      <div className="mt-1">
                        <a href={row.imageUrl} target="_blank" rel="noreferrer">
                          View uploaded image
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-2">
                    {form.ids.length > 2 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removeIdRow(i)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <small className="text-muted">
                Please submit at least two IDs with images (e.g., Aadhaar +
                PAN).
              </small>
            </div>

            {/* Profile photo */}
            <div className="col-12 col-md-4">
              <label className="form-label">Passport-size Photo</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="form-control"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setScanModal({
                    show: true,
                    title: "Scan Passport-size Photo",
                    target: "pp",
                    idIndex: null,
                    file: f,
                    options: {
                      maxWidth: 1024,
                      maxHeight: 1024,
                      maxBytes: 300 * 1024,
                      mimeType: "image/jpeg",
                      initialQuality: 0.85,
                    },
                  });
                }}
              />
              {form.profilePhotoUrl && (
                <div className="mt-1">
                  <a
                    href={form.profilePhotoUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View photo
                  </a>
                </div>
              )}
            </div>

            {/* DOB, Age, Experience */}
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="dateOfBirth">
                Date of Birth
              </label>
              <input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                className="form-control"
                value={form.dateOfBirth}
                onChange={(e) => {
                  const dob = e.target.value;
                  const age = computeAgeYearsLocal(dob);
                  setForm((f) => ({ ...f, dateOfBirth: dob, ageYears: age }));
                }}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="ageYears">
                Age (years)
              </label>
              <input
                id="ageYears"
                name="ageYears"
                type="number"
                min="0"
                className="form-control"
                value={form.ageYears}
                onChange={handleFormChange}
                placeholder="Auto-calculated from DOB; you may override"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="experienceYears">
                Total Experience (years)
              </label>
              <input
                id="experienceYears"
                name="experienceYears"
                type="number"
                min="0"
                className="form-control"
                value={form.experienceYears}
                onChange={handleFormChange}
              />
            </div>

            {/* Jobs (multiple) */}
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">
                  Jobs (add multiple roles)
                </label>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addJobRow}
                >
                  + Add Job
                </button>
              </div>

              {form.jobs.map((job, idx) => {
                const subs = subcatsByCat.get(job.categoryKey) || [];
                const bucketKey = `${job.categoryKey}|${
                  job.subcategoryKey || ""
                }`;
                const titles = jobTitlesByCatSub.get(bucketKey) || [];
                return (
                  <div className="row g-2 align-items-end mb-2" key={idx}>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Category</label>
                      <select
                        className="form-select"
                        value={job.categoryKey}
                        onChange={(e) =>
                          onJobCategoryChange(idx, e.target.value)
                        }
                      >
                        {activeCats.length === 0 ? (
                          <option value="">— No categories —</option>
                        ) : (
                          activeCats.map((c) => (
                            <option key={c.id} value={c.key}>
                              {decodeEntities(c.label || c.key)}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Subcategory</label>
                      <select
                        className="form-select"
                        value={job.subcategoryKey}
                        onChange={(e) =>
                          onJobSubcategoryChange(idx, e.target.value)
                        }
                        disabled={!job.categoryKey}
                      >
                        {!job.categoryKey ? (
                          <option value="">— Select category first —</option>
                        ) : subs.length === 0 ? (
                          <option value="">— No subcategories —</option>
                        ) : (
                          <>
                            <option value="">(None)</option>
                            {subs.map((s) => (
                              <option key={s.id} value={s.key}>
                                {decodeEntities(s.label || s.key)}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Job Title</label>
                      <select
                        className="form-select"
                        value={job.title || ""}
                        onChange={(e) =>
                          handleJobChange(idx, "title", e.target.value)
                        }
                        disabled={!job.categoryKey}
                      >
                        {!job.categoryKey ? (
                          <option value="">— Select category first —</option>
                        ) : titles.length === 0 ? (
                          <option value="">— No services configured —</option>
                        ) : (
                          <>
                            <option value="">(Select)</option>
                            {titles.map((t, i) => (
                              <option key={`${bucketKey}-${i}`} value={t.label}>
                                {t.label}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                      <small className="text-muted">
                        Titles come from Services under the selected
                        Category/Subcategory.
                      </small>
                    </div>
                    <div className="col-12 col-md-2">
                      <label className="form-label">Exp (years)</label>
                      <input
                        className="form-control"
                        type="number"
                        min="0"
                        placeholder="e.g., 2"
                        value={job.experienceYears}
                        onChange={(e) =>
                          handleJobChange(
                            idx,
                            "experienceYears",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="col-12">
                      {form.jobs.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeJobRow(idx)}
                        >
                          Remove Job
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit */}
            <div className="col-12">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={creating}
              >
                {creating ? "Saving..." : "Add Employee"}
              </button>
              <span className="text-muted ms-2">
                Status will be <strong>pending</strong> until approved.
              </span>
              {createError && (
                <div className="alert alert-danger d-inline-block ms-2 py-1 px-2 mb-0">
                  {createError.message || String(createError)}
                </div>
              )}
            </div>
          </div>
        </form>

        <div className="d-flex flex-row gap-5 mt-3">
          <div>
            {/* Scan & Upload modal wiring */}
            <DocumentScanModal
              show={scanModal.show}
              file={scanModal.file}
              title={scanModal.title}
              options={scanModal.options}
              onCancel={() => setScanModal((s) => ({ ...s, show: false }))}
              onConfirm={async (compressedBlob) => {
                try {
                  await ensureSignedIn();
                  const mobileSafe = String(form.mobile || "unknown").replace(
                    /[^\w.-]/g,
                    "_"
                  );
                  if (scanModal.target === "id") {
                    const fileName = `id-${scanModal.idIndex + 1}.jpg`;
                    const { url } = await uploadToStorage(
                      compressedBlob,
                      `employees/${mobileSafe}/ids`,
                      fileName
                    );
                    setForm((prev) => {
                      const ids = [...(prev.ids || [])];
                      if (
                        typeof scanModal.idIndex === "number" &&
                        ids[scanModal.idIndex]
                      ) {
                        ids[scanModal.idIndex] = {
                          ...ids[scanModal.idIndex],
                          imageUrl: url,
                        };
                      }
                      return { ...prev, ids };
                    });
                  } else if (scanModal.target === "pp") {
                    const { url } = await uploadToStorage(
                      compressedBlob,
                      `employees/${mobileSafe}/profile`,
                      `profile.jpg`
                    );
                    setForm((f) => ({ ...f, profilePhotoUrl: url }));
                  }
                } catch (e) {
                  console.error(e);
                  setCreateError(e);
                } finally {
                  setScanModal((s) => ({ ...s, show: false }));
                }
              }}
            />
          </div>
          <div>
            <a
              href="/employees/register"
              className="btn btn-link btn-sm ms-2"
              target="_blank"
              rel="noreferrer"
            >
              Public Registration Link
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
